import { memo, ReactElement } from 'react'

import isEqual from 'lodash/isEqual'
import { useTranslation } from 'react-i18next'

import { updateCampaign } from 'api/Cerebro/orgScoped/amazonAdsApi'
import { useResource } from 'components/EditableFields/localHooks'
import { ARCHIVED } from 'const/resourceStates'
import { userHasManageAmsPermissions } from 'helpers/featurePermissions'
import { usePermissionMessage, useUserHasPermissions } from 'hooks'
import { CampaignGroupBy } from 'types'

import { DeleteField } from '../../SharedFields/DeleteField'

interface Props<RecordType> {
    dataIndex: string[]
    readOnly: boolean
    record: RecordType
    reloadData: () => void
}

function ArchiveCampaignField<RecordType extends CampaignGroupBy>({
    dataIndex = ['campaign'],
    readOnly = false,
    record,
    reloadData,
}: Props<RecordType>): ReactElement {
    const { t } = useTranslation(['table', 'common'])
    const hasPermission = useUserHasPermissions(userHasManageAmsPermissions)
    const permissionMessage = usePermissionMessage()
    const { getFromResource, resourceId } = useResource<RecordType>(
        record,
        dataIndex,
        'campaign'
    )

    const userCanManageCampaign = getFromResource(['user_can_manage'], true)

    const getTooltipTitle = (): string | undefined => {
        if (userCanManageCampaign) {
            return undefined
        }

        return hasPermission
            ? (getFromResource(['user_can_manage_detail']) ?? permissionMessage)
            : permissionMessage
    }

    return (
        <DeleteField
            disabled={!userCanManageCampaign || readOnly}
            okText={t('common:archive', 'Archive')}
            title={t(
                'table:fields.archiveCampaign.confirmTxt',
                'Are you sure you want to archive this Campaign?'
            )}
            tooltipTitle={getTooltipTitle()}
            updateRequestApi={updateCampaign}
            updateRequestCallback={reloadData}
            updateRequestData={resourceId}
            updateRequestParams={{ state: ARCHIVED }}
            updateRequestSuccessMesg={t(
                'table:fields.archiveCampaign.successMsg',
                'Campaign successfully archived'
            )}
        />
    )
}

export default memo(
    ArchiveCampaignField,
    isEqual
) as typeof ArchiveCampaignField
